import { useEffect, useState } from "react";
import ClientPrivateLayout from "@/layouts/ClientPrivateLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import DeleteClientDialog from "@/components/dialogs/DeleteClientDialog";
import { useAuth } from "@/contexts/AuthContext";
import { getRequestOptions } from "@/lib/utils";
import * as z from "zod";
import { Helmet } from "react-helmet";
import { zodResolver } from "@hookform/resolvers/zod";

const AccountSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const formSchema = z.object({
    firstName: z.string().min(2, t("accountSettings.firstNameRequired")),
    lastName: z.string().min(2, t("accountSettings.lastNameRequired")),
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const fetchURL = `${import.meta.env.VITE_BOOKING_CLIENT_ENDPOINT}/${user.id}`;
      const response = await fetch(fetchURL, getRequestOptions("PUT", JSON.stringify(values), "json"));

      if (!response.ok) {
        throw new Error("Failed to update client profile.");
      }

      toast({
        title: t("accountSettings.updateSuccess"),
        description: t("accountSettings.updateSuccessMessage"),
        duration: 5000,
        className: "bg-green-500 text-white border-green-600",
      });
    } catch (error) {
      console.error("Error updating client profile:", error);
    }
  };

  const handleDeleteClientAccount = async () => {

    try {
      const deleteURL = `${import.meta.env.VITE_BOOKING_CLIENT_ENDPOINT}`;
      const body = {
        id: user.id
      };

      const response = await fetch(deleteURL, getRequestOptions("DELETE", JSON.stringify(body), "json"));

      if (!response.ok) {
        throw new Error("Failed to delete account.");
      }

      toast({
        title: t("accountSettings.deleteSuccess"),
        description: t("accountSettings.deleteSuccessMessage"),
        duration: 5000,
        className: "bg-red-500 text-white border-red-600",
      });
      //logout
      localStorage.removeItem("currentUser");
      sessionStorage.clear();
      window.location.href = "/";

    } catch (error) {
      console.error("Error deleting account:", error);
    }
  };

  useEffect(() => {

  }, []);

  return (
    <ClientPrivateLayout>
      <Helmet>
        <title>{t("accountSettings.title")} | FitCalendar</title>
      </Helmet>
      <div className="flex-1 max-w-2xl mx-auto p-8">
        <h1 className="text-3xl font-bold mb-8">{t("accountSettings.editTitle")}</h1>

        {/* Profile Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("accountSettings.firstName")} *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("accountSettings.lastName")} *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button type="submit">{t("common.save")}</Button>
          </form>
        </Form>

        {/* Danger Zone */}
        <div className="mt-12 border-t pt-8">
          <h2 className="text-xl font-semibold text-red-600 mb-2">{t("accountSettings.dangerZone")}</h2>
          <p className="text-sm text-gray-600 mb-4">{t("accountSettings.deleteWarning")}</p>
          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
            {t("accountSettings.deleteAccount")}
          </Button>
        </div>
      </div>
      <DeleteClientDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteClientAccount}
      />
    </ClientPrivateLayout>
  );
};

export default AccountSettings;